import React, { useEffect, useRef, useState } from "react";
import { Container, Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import CommonHeader from "./CommonHeader";
import "../assets/css/argon-dashboard-react.css";
import { fetchXp, fetchLevel } from "apiHandles/user_progress";
import Sidebar from "components/Sidebar/Sidebar.js";
import routes from "routes.js";
import { useGameNavigationBlocker } from "hooks/useGameNavigationBlocker";
import "../assets/css/colors.css";
import { useDarkMode } from 'context/DarkModeProvider.js';
import { useLanguage } from "context/LanguageProvider";

const GameFieldLayout = ({ children, title }) => {
    const [xp, setXp] = useState(0);
    const [level, setLevel] = useState(1);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [continueNavigation, setContinueNavigation] = useState(null);
    const [firstTry, setFirstTry] = useState(false);
    const [gameFinished, setGameFinished] = useState(false);
    const [showHeader, setShowHeader] = useState(true);
    const { darkMode } = useDarkMode();
    const scrollContainerRef = useRef(null);

    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    useEffect(() => {
        const loadXp = async () => {
            const xp = await fetchXp();
            setXp(xp || 0);
        };
        const loadLevel = async () => {
            const level = await fetchLevel();
            setLevel(level || 1);
        };

        loadXp();
        loadLevel();
    }, []);

    useEffect(() => {
        const firstTryFlag = localStorage.getItem("gameFirstTry");
        setFirstTry(firstTryFlag === "true");
    }, []);

    useGameNavigationBlocker(firstTry && !gameFinished, (proceed) => {
        setShowLeaveModal(true);
        setContinueNavigation(() => proceed);
    });

    useEffect(() => {
        const handleGameFinished = () => {
            setGameFinished(true);
        };

        window.addEventListener('gameFinished', handleGameFinished);
        return () => window.removeEventListener('gameFinished', handleGameFinished);
    }, []);

    useEffect(() => {
        if (!firstTry) return;
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "Αν φύγεις τώρα, θα χαθεί η πρόοδός σου και δεν θα μπορείς να ξαναπάρεις XP από αυτό το παιχνίδι!";
            return e.returnValue;
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [firstTry]);

    // Scroll detection για το header
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const handleScroll = () => {
            const scrollTop = scrollContainer.scrollTop;
            // Εμφάνιση header μόνο όταν είμαστε στην κορυφή (0-50px)
            setShowHeader(scrollTop < 50);
        };

        scrollContainer.addEventListener('scroll', handleScroll);
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <>
            <Sidebar
                routes={routes}
                logo={{
                    innerLink: "/admin/index",
                    imgSrc: require("../assets/img/brand/argon-react.png"),
                    imgAlt: "...",
                }}
            />
            <div className="main-content" style={{
                background: darkMode ? "#0f172a" : "linear-gradient(135deg, #f8fafc, #f8fafc)",
                position: "relative",
                overflowX: "hidden" // Prevent horizontal scroll
            }}>
                {/* Header with fixed position */}
                <div style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                    width: "100%",
                    opacity: showHeader ? 1 : 0,
                    transform: showHeader ? 'translateY(0)' : 'translateY(-100%)',
                    transition: 'opacity 0.3s ease, transform 0.3s ease',
                    backgroundColor: darkMode ? "#0f172a" : "var(--lightModeBack)"
                }}>
                    <CommonHeader />
                </div>

                {/* Main content area */}
                <div
                    ref={scrollContainerRef}
                    style={{
                        minHeight: "calc(100vh - 70px)",
                        padding: "1rem 30px",
                    }}
                >
                    <Container fluid>{children}</Container>
                </div>
            </div>

            {/* Leave warning modal */}
            <Modal isOpen={showLeaveModal} centered contentClassName="no-shadow-modal">
                <div style={{
                    background: darkMode ? "#1e293b" : "var(--lightModeModal)",
                    borderRadius: '0.5rem',
                    maxWidth: "90vw"
                }}>
                    <ModalHeader
                        style={{
                            fontSize: "clamp(1.2rem, 4vw, 1.5rem)",
                            textAlign: "center",
                            color: "#cf3838ff",
                            justifyContent: "center"
                        }}>
                        {selLanguage ? "Προειδοποίηση!" : "Warning!"}
                    </ModalHeader>
                    <ModalBody style={{ padding: "clamp(1rem, 3vw, 2rem)" }}>
                        <p style={{
                            textAlign: "center",
                            color: darkMode ? "#e4e4e4ff" : "#1b1b1bff",
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                            lineHeight: "1.5",
                            marginBottom: "1.5rem",
                        }}>
                            {selLanguage ? (
                                <>
                                    Αν φύγεις τώρα, θα χαθεί η πρόοδός σου και δεν θα μπορείς να ξαναπάρεις XP από αυτό το παιχνίδι!<br />
                                    Θέλεις να συνεχίσεις ή να φύγεις;
                                </>
                            ) : (
                                <>
                                    If you leave now, your progress will be lost and you won't be able to earn XP from this game again!<br />
                                    Do you want to continue or leave?
                                </>
                            )}
                        </p>
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "1rem",
                            flexWrap: "wrap"
                        }}>
                            <Button
                                onClick={() => {
                                    setShowLeaveModal(false);
                                    if (continueNavigation) continueNavigation();
                                }}
                                style={{
                                    background: "linear-gradient(135deg, #b81e1eff 0%, #7c0505ff 100%)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "0.5rem",
                                    padding: "0.75rem 1.5rem",
                                    fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                                    minWidth: "100px"
                                }}
                            >
                                {selLanguage ? "Φύγε" : "Leave"}
                            </Button>
                            <Button
                                color="primary"
                                onClick={() => setShowLeaveModal(false)}
                                style={{
                                    borderRadius: "0.5rem",
                                    padding: "0.75rem 1.5rem",
                                    fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                                    minWidth: "100px",
                                    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                                }}
                            >
                                {selLanguage ? "Μείνε στο Παιχνίδι" : "Stay in Game"}
                            </Button>
                        </div>
                    </ModalBody>
                </div>
            </Modal>
        </>
    );
};

export default GameFieldLayout;